import { useState } from "react";
import { ChevronDown, ChevronUp } from "../../Components/ui/icons";
import {
  PageContainer,
  PageTitle,
  SectionsContainer,
  Section,
  SectionLabel,
  SectionTitle,
  SectionDescription,
  AccordionList,
  AccordionItem,
  AccordionHeader,
  AccordionItemTitle,
  AccordionBody,
} from "./styles";

type AccordionEntry = {
  title: string;
  content: string;
};

const CATEGORIZACAO_ITEMS: AccordionEntry[] = [
  {
    title: "Teórico",
    content:
      "Descreve o recurso disponível para futuros usos de energia, sem considerar perdas por conversão ou transmissão. Serve de guia para a utilização plena de renováveis em outras aplicações presentes — como transporte, responsável por 4% do total — e futuras, que promovam melhor qualidade de vida e segurança. Aplicável a: Eólico e Solar.",
  },
  {
    title: "Geográfico",
    content:
      "Potencial intermediário. Considera as áreas próprias para exploração de energia, incorporando como exclusões: áreas com declividade superior a 17°, áreas de preservação ambiental, corpos d'água, zonas urbanas e florestas. Aplicável a: Eólico e Solar.",
  },
  {
    title: "Técnico",
    content:
      "Potencial do estado presente, com hipótese de que haveria conexão para todos os empreendimentos de geração. Inclui perdas por transformação de energia, por disponibilidade e perdas aerodinâmicas no cálculo. O potencial técnico solar adiciona exclusão de áreas com GHI abaixo de 5,8 kWh/m²/dia e de áreas agrícolas. Aplicável a: Eólico e Solar.",
  },
  {
    title: "Econômico I",
    content:
      "Trata do potencial presente com restrição de custos pela distância à Rede Básica. No eólico: até 100 km. No solar: até 30 km. Aplicável a: Eólico e Solar.",
  },
  {
    title: "Econômico II",
    content:
      "Potencial presente com restrição de distância à Rede de Subtransmissão Neoenergia. Considera projetos até 10 km da rede de distribuição, explorando a capilaridade da malha da Neoenergia. Aplicável a: Solar.",
  },
  {
    title: "Econômico III",
    content:
      "Potencial que trata da geração distribuída, utilizando somente as áreas urbanizadas do Estado e conexão com a rede de baixa tensão. Exercício para a autonomia energética dos municípios (Res. ANEEL nº 482/2012). Aplicável a: Solar.",
  },
  {
    title: "Híbrido",
    content:
      "Trata do uso de eólica e solar conjuntamente, com o uso da mesma estrutura de conexão do eólico econômico. A eólica é complementada com solar até atingir 100% de produção de energia, aproveitando a complementariedade entre o ciclo noturno de vento e a produção diurna solar. Aplicável a: Eólico e Solar.",
  },
];

const CONCEITOS_ITEMS: AccordionEntry[] = [
  {
    title: "Área propícia",
    content:
      "Porção do território resultante após a aplicação de todas as exclusões geográficas, ambientais e de recurso energético (velocidade de vento mínima ou irradiação solar mínima). É sobre essa área que se calculam a densidade de potência instalável, a energia gerada e o fator de capacidade. Tudo que fica fora dela é tratado como área de exclusão.",
  },
  {
    title: "Área de exclusão",
    content:
      "Qualquer porção do território retirada do cálculo de potencial por razões legais, ambientais, técnicas ou econômicas. Os critérios variam conforme o tipo e a camada de potencial, mas em geral incluem: áreas de proteção ambiental, terras indígenas, quilombos, corpos d'água e suas faixas de preservação, zonas urbanas e restrições de declividade. Nos potenciais econômicos, somam-se critérios de distância à rede elétrica.",
  },
  {
    title: "Densidade de potência instalável",
    content:
      "Quantidade de potência (em MW ou MWp) instalável por quilômetro quadrado de área propícia. No caso eólico, é determinada pelo espaçamento entre aerogeradores (layout) e pelo porte da turbina adotada. No caso solar, pelo arranjo dos painéis, pelo espaçamento entre fileiras e pela relação AC/DC. Varia significativamente conforme o potencial: de 33,6 MW/km² no eólico teórico até 2,4 MW/km² no eólico econômico.",
  },
  {
    title: "Fator de capacidade",
    content:
      "Relação percentual entre a energia efetivamente gerada e a energia que seria produzida se o equipamento operasse em plena carga durante todo o ano. Indica o aproveitamento real do recurso energético disponível. Para o eólico técnico a 140 m em Pernambuco, o fator de capacidade líquido supera 46%, valor excelente para projetos onshore. Para o solar fotovoltaico, adota-se um fator de capacidade fixo de 25% no cálculo do potencial híbrido.",
  },
  {
    title: "Rede Básica de transmissão",
    content:
      "Conjunto de linhas de alta tensão que compõem a espinha dorsal do Sistema Interligado Nacional (SIN). É utilizada como referência nos potenciais econômicos: no eólico, o critério é de distância máxima de 100 km; no solar econômico I, de até 30 km. Ressalta-se que os potenciais econômicos não avaliam a capacidade de carga das subestações, apenas estabelecem um critério de custo de implantação da conexão.",
  },
  {
    title: "Rede de distribuição (Neoenergia)",
    content:
      "Malha de linhas de média e baixa tensão operada pela distribuidora local (Neoenergia em Pernambuco), responsável pelo fornecimento de energia aos consumidores finais. No potencial solar econômico II, considera-se como premissa a distância de até 10 km à rede de distribuição da Neoenergia, uma das mais estruturadas malhas de distribuição do país, para viabilizar projetos de médio porte.",
  },
  {
    title: "Geração distribuída",
    content:
      "Modelo de geração elétrica em que pequenos produtores — residências, comércios e indústrias — instalam painéis fotovoltaicos em seus imóveis e injetam os excedentes na rede de distribuição local, recebendo créditos de energia. Regulamentada no Brasil pela Resolução Normativa ANEEL nº 482/2012 e atualizada pela nº 687/2015, representa a descentralização da produção elétrica, transferindo-a das mãos de grandes agências e empresas para milhões de pequenos geradores.",
  },
  {
    title: "Complementariedade eólico-solar",
    content:
      "Característica do regime de ventos e irradiação solar em Pernambuco em que o ciclo noturno de vento, dominante em praticamente todo o Estado, pode ser complementado pela produção diurna de energia solar fotovoltaica. Essa complementariedade é a base conceitual do potencial híbrido: o mesmo ponto de conexão à rede pode escoar energia eólica à noite e energia solar durante o dia, sem necessidade de ampliar a infraestrutura de transmissão.",
  },
  {
    title: "DGC (Desempenho Global de Continuidade)",
    content:
      "Indicador sintético criado pela ANEEL para avaliar a qualidade global do fornecimento de energia de cada distribuidora e elaborar o Ranking Anual de Continuidade. Em vez de olhar o DEC e o FEC isoladamente, o DGC mede o quanto a distribuidora cumpriu ou extrapolou os limites globais regulatórios fixados no PRODIST (Procedimentos de Distribuição). O cálculo compara a duração (DEC) e a frequência (FEC) reais das interrupções de uma concessionária com as suas respectivas metas regulatórias para um determinado ano, ponderando-as pelo tamanho do mercado (número de consumidores de cada conjunto).",
  },
];

const EOLICO_ITEMS: AccordionEntry[] = [
  {
    title: "Potencial eólico teórico",
    content:
      "Visão de longo prazo que representa o máximo de energia eólica disponível no território, assumindo que a tecnologia futura permitirá o aproveitamento de potenciais mais baixos com maior eficiência. A energia é considerada totalmente aproveitável, sem perdas por conversão ou transmissão. O único critério de exclusão é a velocidade do vento abaixo de 6 m/s. Contempla também a perspectiva de que a energia eólica poderá atender demandas além da eletricidade, como o setor de transportes — responsável por cerca de 30% do consumo mundial de energia.",
  },
  {
    title: "Potencial eólico geográfico",
    content:
      "Valor intermediário entre o teórico e o técnico. Parte do potencial teórico após a aplicação de restrições geográficas e ambientais: declividade superior a 17°, áreas de proteção ambiental, terras indígenas, florestas, corpos d'água com faixas de preservação de 100 a 600 m (conforme a margem) e zonas urbanas com afastamento de 500 m. Responde à pergunta: qual seria o aproveitamento considerando todo o espaço legalmente disponível?",
  },
  {
    title: "Potencial eólico técnico",
    content:
      "Representa o estado presente: o que poderia ser instalado em Pernambuco se houvesse condição de conexão para toda e qualquer área propícia. Calculado com aerogeradores em uso atual no Brasil e com o espaçamento de mercado de 3D × 20D (3 diâmetros de rotor em colunas e 20 em linhas). Incorpora perdas por transformação elétrica (3%), indisponibilidade da máquina (2,5%) e perdas aerodinâmicas de esteira (5%), resultando numa densidade de ~3,2 MW/km².",
  },
  {
    title: "Potencial eólico econômico",
    content:
      "Acrescenta ao potencial técnico a restrição de distância máxima de 100 km à Rede Básica de transmissão, como critério de custo de implantação. Não contempla estudo de viabilidade de conexão à rede e sim um critério econômico de distância. Incorpora também espaçamento adicional de 3.000 m entre blocos de 300 MW, resultando numa densidade de ~2,4 MW/km².",
  },
];

const SOLAR_ITEMS: AccordionEntry[] = [
  {
    title: "Potencial solar teórico",
    content:
      "Visão de longo prazo que representa a transformação de toda a radiação solar disponível no território, pressupondo que evoluções tecnológicas permitirão aproveitar potenciais mais baixos com maior eficiência. A energia é considerada totalmente aproveitável, sem perdas por conversão ou transmissão. Os únicos critérios de exclusão são a declividade igual ou superior a 14% e as regiões de florestas. Áreas urbanas e corpos d'água são considerados aproveitáveis no horizonte futuro contemplado por este potencial.",
  },
  {
    title: "Potencial solar geográfico",
    content:
      "Valor intermediário entre o teórico e o técnico. Parte do potencial teórico incorporando as mesmas restrições geográficas e ambientais do eólico geográfico: áreas de proteção ambiental, terras indígenas, florestas, corpos d'água com faixas de preservação de 100 a 600 m e zonas urbanas com afastamento de 500 m.",
  },
  {
    title: "Potencial solar técnico",
    content:
      "Representa o estado presente com hipótese de conexão disponível para todos os empreendimentos. Calculado sobre as áreas do potencial geográfico com duas restrições adicionais: exclusão das áreas agrícolas (para evitar competição com a produção de alimentos) e limitação a regiões com Radiação Solar Global (GHI) igual ou superior a 5,8 kWh/m²/dia — valor de corte que representa a radiação média anual dos projetos aprovados nos leilões de energia no Brasil.",
  },
  {
    title: "Potencial solar econômico I",
    content:
      "Derivado do potencial técnico com a restrição adicional de distância máxima de 30 km à Rede Básica de transmissão. Trata dos empreendimentos fotovoltaicos de grande porte conectados à rede de alta tensão. O critério de distância reflete o custo de implantação da conexão elétrica, sem inferências sobre a capacidade de carga das subestações.",
  },
  {
    title: "Potencial solar econômico II",
    content:
      "Variante voltada a empreendimentos conectados à rede de distribuição da Neoenergia. Substitui o raio de 30 km da Rede Básica por um raio de até 10 km da rede de distribuição, explorando a capilaridade dessa infraestrutura para viabilizar projetos de médio porte em localidades não atendidas pelo critério do Econômico I.",
  },
  {
    title: "Potencial solar econômico III (geração distribuída)",
    content:
      "Potencial voltado à geração distribuída de energia solar fotovoltaica. Considera exclusivamente as manchas urbanas dos municípios, sem qualquer outra exclusão e sem restrição de magnitude de radiação solar. É um exercício para o planejamento do uso de radiação solar com vistas à autonomia energética dos municípios, à luz da Resolução Normativa ANEEL nº 482/2012 e sua atualização pela nº 687/2015.",
  },
];

const HIBRIDO_ITEMS: AccordionEntry[] = [
  {
    title: "Potencial híbrido",
    content:
      "Potencial que combina geração eólica e solar fotovoltaica em uma mesma estrutura de conexão à rede. Como os parques eólicos são dimensionados para entregar a potência nominal plena instalada, há margem de aproveitamento do mesmo ponto de conexão para uma instalação solar sem ampliar a infraestrutura elétrica. O cálculo parte da área útil do potencial eólico econômico, excluindo as faixas de sombreamento potencial do aerogerador sobre os módulos fotovoltaicos e as áreas de segurança para manutenção das turbinas. A energia solar complementa a produção eólica até atingir 100% da capacidade do ponto de conexão, assumindo fator de capacidade fixo de 25% para o fotovoltaico.",
  },
  {
    title: "Sombreamento de aerogerador",
    content:
      "Faixa de exclusão ao redor de cada aerogerador dentro de um parque híbrido, onde a instalação de painéis fotovoltaicos não é considerada viável devido ao sombreamento potencial causado pelo rotor e pela torre sobre os módulos solares. Essa faixa, junto com as áreas de segurança para manutenção dos aerogeradores, é descontada da área útil disponível para o componente solar no cálculo do potencial híbrido.",
  },
];

const PARAMS_EOLICO_ITEMS: AccordionEntry[] = [
  {
    title: "Parâmetros de Weibull",
    content:
      "Conjunto de dois parâmetros estatísticos (fator de escala c e fator de forma k) que descrevem a distribuição de frequências da velocidade do vento em um determinado local e altura. São usados em conjunto com a curva de potência do aerogerador para calcular a energia gerada ao longo do ano.",
  },
  {
    title: "Curva de potência",
    content:
      "Gráfico fornecido pelo fabricante que relaciona a velocidade do vento com a potência elétrica gerada pelo aerogerador. É a base para o cálculo da energia gerada em cada faixa de velocidade, combinada com a distribuição de Weibull local.",
  },
  {
    title: "Perdas de esteira (wake loss)",
    content:
      "Redução de energia causada pela turbulência gerada atrás de cada aerogerador, que afeta as turbinas posicionadas a sotavento. Nos potenciais técnico e econômico eólicos, adota-se 5% de perdas aerodinâmicas de esteira, mesmo com espaçamento de 20 diâmetros de rotor (20D) entre filas na direção do vento.",
  },
  {
    title: "Layout de aerogeradores (3D × 7D / 3D × 20D)",
    content:
      "Configuração espacial dos aerogeradores em um parque eólico, expressa em múltiplos do diâmetro do rotor (D). O layout 3D × 7D é compacto, usado nos potenciais teórico e geográfico (maior densidade). O layout 3D × 20D é conservador, adotado nos potenciais técnico e econômico para reduzir perdas de esteira.",
  },
  {
    title: "Declividade limite eólica (17°)",
    content:
      "Critério de exclusão topográfica para o potencial eólico. Terrenos com inclinação superior a 17° são excluídos da área propícia, pois declives acentuados causam separação do escoamento do vento, comprometendo a performance e a segurança estrutural dos aerogeradores.",
  },
];

const PARAMS_SOLAR_ITEMS: AccordionEntry[] = [
  {
    title: "Radiação Solar Global (GHI — Global Horizontal Irradiance)",
    content:
      "Total de energia solar (componentes direta e difusa) recebida por uma superfície horizontal por unidade de área, expressa em kWh/m²/dia ou kWh/m²/ano. É o principal dado meteorológico de entrada para o cálculo do potencial solar fotovoltaico. O valor de corte adotado a partir do potencial técnico é de 5,8 kWh/m²/dia, correspondente à radiação média anual dos projetos aprovados nos leilões de energia no Brasil.",
  },
  {
    title: "Declividade limite solar (14% / 8°)",
    content:
      "Critério de exclusão topográfica para o potencial solar. No potencial teórico, o limite é de 14% de declividade; nos demais potenciais, adota-se o equivalente a 8°. Terrenos acima desses limites são excluídos da área propícia, pois dificultam a instalação e manutenção de painéis fotovoltaicos e reduzem o aproveitamento da radiação incidente.",
  },
  {
    title: "Eficiência média do conjunto",
    content:
      "Percentual da energia solar incidente convertida em energia elétrica entregue à rede, considerando perdas do painel, do inversor, do cabeamento e de outros fatores de sistema. O atlas adota 25% como valor conservador; sistemas com rastreador solar (tracker) podem alcançar facilmente 30%.",
  },
  {
    title: "Relação AC/DC (inverter loading ratio)",
    content:
      "Razão entre a potência nominal do inversor (saída em corrente alternada, AC) e a potência de pico instalada nos painéis (entrada em corrente contínua, DC). O valor adotado de 1,2 significa que para cada 1 MW de inversor são conectados 1,2 MWp de painéis, prática comum para otimizar o aproveitamento do inversor nos períodos de menor irradiação.",
  },
  {
    title: "Seguidor solar (tracker)",
    content:
      "Mecanismo que ajusta a orientação dos painéis fotovoltaicos ao longo do dia para maximizar a captação de radiação solar. Sistemas com tracker podem aumentar a geração em 20–30% em relação a instalações fixas, elevando a eficiência média do conjunto de 25% para cerca de 30%.",
  },
  {
    title: "Mancha urbana",
    content:
      "Perímetro contínuo de área edificada de um município. No potencial solar econômico III (geração distribuída), é a única área considerada propícia, representando o território onde a microgeração e minigeração solar conectada à rede de distribuição é viável nos termos da regulação da ANEEL.",
  },
];

const METODOLOGIA_ITEMS: AccordionEntry[] = [
  { title: "Como calculamos o Potencial Solar do Estado", content: "" },
  { title: "Como calculamos o Potencial Eólico do Estado", content: "" },
];

function Accordion({ items }: { items: AccordionEntry[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <AccordionList>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <AccordionItem key={item.title}>
            <AccordionHeader
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
            >
              <AccordionItemTitle>{item.title}</AccordionItemTitle>
              {isOpen ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
            </AccordionHeader>
            {isOpen && item.content && (
              <AccordionBody>{item.content}</AccordionBody>
            )}
          </AccordionItem>
        );
      })}
    </AccordionList>
  );
}

const SECTIONS = [
  {
    title: "Categorização dos Potenciais",
    description:
      "Os diferentes níveis de análise do potencial energético renovável e suas respectivas restrições.",
    items: CATEGORIZACAO_ITEMS,
  },
  {
    title: "Conceitos Gerais",
    description:
      "Definições fundamentais utilizadas nas análises de potencial energético.",
    items: CONCEITOS_ITEMS,
  },
  {
    title: "Potenciais Eólicos",
    description:
      "Detalhamento de cada camada de potencial de energia eólica em Pernambuco.",
    items: EOLICO_ITEMS,
  },
  {
    title: "Potenciais Solares",
    description:
      "Detalhamento de cada camada de potencial de energia solar fotovoltaica em Pernambuco.",
    items: SOLAR_ITEMS,
  },
  {
    title: "Potencial Híbrido",
    description:
      "Combinação de geração eólica e solar fotovoltaica em uma mesma estrutura de conexão.",
    items: HIBRIDO_ITEMS,
  },
  {
    title: "Parâmetros Técnicos Eólicos",
    description:
      "Variáveis e configurações utilizadas no cálculo do potencial eólico.",
    items: PARAMS_EOLICO_ITEMS,
  },
  {
    title: "Parâmetros Técnicos Solares",
    description:
      "Variáveis e configurações utilizadas no cálculo do potencial solar.",
    items: PARAMS_SOLAR_ITEMS,
  },
];

export function FAQ() {
  return (
    <PageContainer>
      <PageTitle>Glossário</PageTitle>
      <SectionsContainer>
        {SECTIONS.map((section) => (
          <Section key={section.title}>
            <SectionLabel>
              <SectionTitle>{section.title}</SectionTitle>
              <SectionDescription>{section.description}</SectionDescription>
            </SectionLabel>
            <Accordion items={section.items} />
          </Section>
        ))}
      </SectionsContainer>
    </PageContainer>
  );
}
